/**
 * Carry a marquee selection from the current canvas to a fresh blank canvas.
 *
 * The new document inherits the source document's provenance because each
 * object keeps its own origin: reconstructed (model), user (stylus), or
 * imported (SVG interchange). Copying reconstructed geometry onto a page that
 * claims "no model ran" would be a provenance lie, so model metadata travels
 * with the objects. Page geometry (size) is preserved so the carried region
 * keeps its coordinates and can be moved/scaled by the user afterwards.
 */

import { createBlankCIMDocument, validateCIMDocument, type CIMDocument } from './model.ts';

export interface TransferTarget {
  documentId: string;
  revisionId: string;
}

export function buildTransferredDocument(
  source: CIMDocument,
  objectIds: readonly string[],
  target: TransferTarget
): CIMDocument {
  const idSet = new Set(objectIds);
  const picked = source.objects
    .filter((o) => idSet.has(o.id))
    .map((o) => structuredClone(o));

  const doc = createBlankCIMDocument(target.documentId, target.revisionId);
  doc.page = { ...doc.page, width: source.page.width, height: source.page.height };
  doc.objects = picked;
  doc.provenance = structuredClone(source.provenance);
  validateCIMDocument(doc);
  return doc;
}

/**
 * Visibility policy for the floating "Boş canvasa geçir" selection option.
 *
 * The option is a selection-scoped affordance: it appears only while a real
 * selection exists and the user is in a selection tool (select or marquee
 * lasso). Pen and eraser gestures must not show it — a pen stroke is not a
 * selection and a floating chip mid-draw would be noise.
 */
export function shouldShowBlankCanvasOption(
  selectedCount: number,
  tool: string,
  hasDocument: boolean
): boolean {
  return hasDocument && selectedCount > 0 && (tool === 'select' || tool === 'lasso');
}
